/**
 * @file Schema utilities for JSON Schema processing and manipulation
 * @version 1.0.0
 * @since 2025-08-30
 */

const { getAjv } = RxDB;

// Get Ajv instance for schema validation
const ajv = getAjv();
ajv.addVocabulary([
  'editable',
  'editableBy',
  'readable',
  'createNewItem',
  'displayMode',
  'displayLabel',
  'displayFieldsLabels',
  'isDeletable',
  'defaults',
  'mode',
  'deletable',
  'timezone',
  'sortable',
  'movable',
  'variant',
]);

// Add custom format for color (hex color validation)
ajv.addFormat('color', {
  validate: value => /^#[0-9A-Fa-f]{6}$/.test(value),
});
ajv.addFormat('textarea', {
  validate: value => typeof value === 'string',
});
ajv.addFormat('password', {
  validate: value => typeof value === 'string',
});

/**
 * Apply schema defaults to config object recursively
 * @param {object} schema - JSON Schema with default values
 * @param {object} config - Config object to apply defaults to
 * @returns {object} Config object with defaults applied
 */
export function applySchemaDefaults(schema, config = {}) {
  if (!schema || !schema.properties) return config;

  const result = { ...config };

  function applyDefaults(schemaObj, configObj, path = []) {
    if (!schemaObj.properties) return;

    for (const [key, propSchema] of Object.entries(schemaObj.properties)) {
      const currentPath = [...path, key];

      if (propSchema.type === 'object' && propSchema.properties) {
        if (!configObj[key]) configObj[key] = {};
        applyDefaults(propSchema, configObj[key], currentPath);
      } else if (Object.prototype.hasOwnProperty.call(propSchema, 'default')) {
        const currentValue = currentPath.reduce((obj, k) => obj?.[k], result);
        if (currentValue === undefined) {
          let target = result;
          for (let i = 0; i < currentPath.length - 1; i++) {
            const part = currentPath[i];
            if (!target[part]) target[part] = {};
            target = target[part];
          }
          target[currentPath[currentPath.length - 1]] = propSchema.default;
        }
      }
    }
  }

  applyDefaults(schema, result);
  return result;
}

/**
 * Apply path-based customizations to schema recursively
 * @param {object} baseSchema - The schema to customize
 * @param {object} customizations - Object with path-based customizations
 * @param {string} basePath - Current path for nested customizations
 * @returns {object} Customized schema
 */
export function applySchemaCustomizations(
  baseSchema,
  customizations = {},
  basePath = ''
) {
  let customizedSchema = { ...baseSchema };

  // Apply customizations for this path
  if (customizations[basePath]) {
    customizedSchema = { ...customizedSchema, ...customizations[basePath] };
  }

  // Recursively apply to nested objects
  if (customizedSchema.type === 'object' && customizedSchema.properties) {
    customizedSchema.properties = { ...customizedSchema.properties };

    Object.keys(customizedSchema.properties).forEach(fieldName => {
      const fieldPath = basePath ? `${basePath}.${fieldName}` : fieldName;
      customizedSchema.properties[fieldName] = applySchemaCustomizations(
        customizedSchema.properties[fieldName],
        customizations,
        fieldPath
      );
    });
  }

  // Apply to array items schema
  if (customizedSchema.type === 'array' && customizedSchema.items) {
    const itemsPath = basePath ? `${basePath}.items` : 'items';
    customizedSchema.items = applySchemaCustomizations(
      customizedSchema.items,
      customizations,
      itemsPath
    );
  }

  return customizedSchema;
}

/**
 * Get type-appropriate default value for a schema
 * @param {object} schema - JSON Schema
 * @param {boolean} includeOptional - Whether to include optional fields
 * @returns {any} Default value for the schema type
 */
export function getSchemaDefaultValue(schema, includeOptional = false) {
  // Use explicit default if present
  if (Object.prototype.hasOwnProperty.call(schema, 'default')) {
    return schema.default;
  }

  // Generate type-appropriate defaults
  switch (schema.type) {
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return schema.minimum !== undefined ? schema.minimum : 0;
    case 'string':
      // Handle format-specific defaults
      switch (schema.format) {
        case 'color':
          return '#000000';
        case 'date': {
          return new Date().toISOString().split('T')[0];
        }
        case 'date-time': {
          return new Date().toISOString();
        }
        case 'email':
          return '';
        case 'url':
          return '';
        default:
          return '';
      }
    case 'array':
      return [];
    case 'object':
      // Generate object with default values for all properties
      if (schema.properties) {
        const defaultObj = {};
        Object.entries(schema.properties).forEach(([key, propSchema]) => {
          // Only add required fields or fields with explicit defaults
          if (
            (schema.required && schema.required.includes(key)) ||
            Object.prototype.hasOwnProperty.call(propSchema, 'default') ||
            includeOptional
          ) {
            defaultObj[key] = getSchemaDefaultValue(propSchema);
          }
        });
        return defaultObj;
      }
      return {};
    default:
      return undefined;
  }
}

/**
 * Create a default document/item value with optional createNewItem callback
 * @param {object} schema - JSON Schema definition
 * @param {Function} getAttribute - Optional attribute resolver for createNewItem callback
 * @returns {any} Default document value
 */
export function createDefaultDocument(schema, getAttribute = null) {
  let defaultValue = getSchemaDefaultValue(schema, true);

  if (getAttribute) {
    const createNewItem = getAttribute('createNewItem');
    if (createNewItem) {
      defaultValue = createNewItem(defaultValue);
    }
  }

  return defaultValue;
}

// Compiled validators are cached so ajv.compile() is only called once per schema object.
const _validatorCache = new WeakMap();

/**
 * Validate a value against a schema using Ajv
 * @param {any} value - Value to validate
 * @param {object} schema - JSON Schema to validate against
 * @returns {string[]} Array of validation error messages (empty if valid)
 */
export function validateSchemaValue(value, schema) {
  if (!schema) return [];

  try {
    let validate = _validatorCache.get(schema);
    if (!validate) {
      validate = ajv.compile(schema);
      _validatorCache.set(schema, validate);
    }
    const valid = validate(value);

    if (!valid) {
      return validate.errors.map(err => `${err.instancePath} ${err.message}`);
    }

    return [];
  } catch (err) {
    return [`Schema validation error: ${err.message}`];
  }
}

/**
 * Deep clone a schema to avoid mutations
 * @param {object} schema - Schema to clone
 * @returns {object} Cloned schema
 */
export function cloneSchema(schema) {
  return JSON.parse(JSON.stringify(schema));
}
